import { useEffect, useState } from "react";

import {
    Avatar,
    Badge,
    Table,
    Text,
    VStack,
    HStack,
    Menu,
    IconButton,
} from "@chakra-ui/react";

import {
    EllipsisVertical,
    FileText,
    CarTaxiFront,
    Eye,
    IdCard,
} from "lucide-react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    registerDriverForUserAction,
    updateDriverForPartnerAction,
    registerVehicleForUserAction,
    updateVehicleForPartnerAction,
    fetchRecruitedVehiclesAction,
} from "../../../state/actions/driverActions";

import DataTable from "../../../components/DataTable";
import PartnerMilestone from "./PartnerMilestone";
import AddDriverDocumentsDrawer from "./AddDriverDocumentsDrawer";
import AddDriverVehicleDrawer from "./AddDriverVehicleDrawer";

import {
    fetchReferredDriversAction,
} from "../../../state/actions/driverPartnerActions";

import { toaster } from "../../../components/toaster";
import ActivityToast from "../../../components/ActivityToast";

import ViewDriverDrawer from "./ViewDriverDrawer";
import ViewVehicleDrawer from "./ViewVehicleDrawer";
import EmptyState from "../../../components/EmptyState";


const badgeColor = {
    Approved: "green",
    Pending: "yellow",
    Active: "blue",
    Inactive: "orange",
    Suspended: "red",
    Rejected: "red",
    "Not Submitted": "gray",
};


const columns = [
    {
        key: "partner",
        label: "Driver",
    },
    {
        key: "joined",
        label: "Date Registered",
    },
    {
        key: "accountStatus",
        label: "Account Status",
    },
    {
        key: "status",
        label: "Document Verification",
    },
    {
        key: "milestone",
        label: "Milestone Progress",
    },
    {
        key: "earnings",
        label: "Earned",
        align: "end",
    },
    {
        key: "actions",
        label: "Actions",
        align: "end",
    },
];


const PartnersTable = ({
    drivers = [],
    milestones = [],
    pendingCommissions = [],
    payouts = [],
}) => {

    const [
        selectedDriver,
        setSelectedDriver,
    ] = useState(null);

    const [
        selectedVehicle,
        setSelectedVehicle,
    ] = useState(null);

    const dispatch =
        useDispatch();

    const {
        drivers: driverDetails,
        registeringDriver,
        registeringVehicle,
        updatingDriver,
        updatingVehicle,
        recruitedVehicles,
    } = useSelector(
        state => state.driver
    );

    const {
        user: authUser,
        isAuthenticated,
    } = useSelector(
        state => state.auth
    );

    const [
        isDriverOpen,
        setDriverOpen,
    ] = useState(false);

    const [
        isDocumentsOpen,
        setDocumentsOpen,
    ] = useState(false);

    const [
        isVehicleOpen,
        setVehicleOpen,
    ] = useState(false);

    const [
        isVehicleDetailsOpen,
        setVehicleDetailsOpen,
    ] = useState(false);


    useEffect(() => {

        dispatch(
            fetchRecruitedVehiclesAction(
                1,
                50
            )
        );

    }, [
        dispatch,
    ]);


    /*
    |--------------------------------------------------------------------------
    | REGISTER DRIVER
    |--------------------------------------------------------------------------
    */

    const handleRegisterDriver =
        async (
            driver,
            form
        ) => {

            const result =
                await dispatch(
                    registerDriverForUserAction(
                        driver.user_id,
                        form
                    )
                );

            if (
                result.success
            ) {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="success"
                            title="Driver Registered"
                            description="The driver profile and documents were submitted successfully."
                            timestamp="Just now"
                        />
                    ),
                });

                dispatch(
                    fetchReferredDriversAction(
                        1,
                        10
                    )
                );

                setDocumentsOpen(
                    false
                );

            } else {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="error"
                            title="Registration Failed"
                            description={
                                result.error ||
                                "Unable to register driver."
                            }
                            timestamp="Just now"
                        />
                    ),
                });

            }

            return result;
        };


    /*
    |--------------------------------------------------------------------------
    | REGISTER VEHICLE
    |--------------------------------------------------------------------------
    */

    const handleRegisterVehicle =
        async (
            driver,
            form
        ) => {

            const result =
                await dispatch(
                    registerVehicleForUserAction(
                        driver.user_id,
                        form
                    )
                );

            if (
                result.success
            ) {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="success"
                            title="Vehicle Registered"
                            description="The vehicle details and supporting documents were submitted successfully."
                            timestamp="Just now"
                        />
                    ),
                });

                dispatch(
                    fetchReferredDriversAction(
                        1,
                        10
                    )
                );

                dispatch(
                    fetchRecruitedVehiclesAction(
                        1,
                        50
                    )
                );

                setVehicleOpen(
                    false
                );

            } else {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="error"
                            title="Registration Failed"
                            description={
                                result.error ||
                                "Unable to register vehicle."
                            }
                            timestamp="Just now"
                        />
                    ),
                });

            }

            return result;
        };


    /*
    |--------------------------------------------------------------------------
    | UPDATE DRIVER
    |--------------------------------------------------------------------------
    */

    const handleUpdateDriver =
        async (
            driver,
            form
        ) => {

            console.log(
                "UPDATING DRIVER:",
                driver
            );

            const result =
                await dispatch(
                    updateDriverForPartnerAction(
                        driver.driver_id,
                        form
                    )
                );

            if (
                result.success
            ) {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="success"
                            title="Driver Updated"
                            description="The driver information and documents were updated successfully."
                            timestamp="Just now"
                        />
                    ),
                });

                dispatch(
                    fetchReferredDriversAction(
                        1,
                        10
                    )
                );

                setDriverOpen(
                    false
                );

            } else {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="error"
                            title="Update Failed"
                            description={
                                result.error ||
                                "Unable to update driver."
                            }
                            timestamp="Just now"
                        />
                    ),
                });

            }

            return result;
        };


    /*
    |--------------------------------------------------------------------------
    | UPDATE VEHICLE
    |--------------------------------------------------------------------------
    */

    const handleUpdateVehicle =
        async (
            vehicle,
            form
        ) => {

            console.log(
                "UPDATING VEHICLE:",
                vehicle
            );

            const result =
                await dispatch(
                    updateVehicleForPartnerAction(
                        vehicle.vehicle_id,
                        form
                    )
                );

            if (
                result.success
            ) {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="success"
                            title="Vehicle Updated"
                            description="The vehicle information and documents were updated successfully."
                            timestamp="Just now"
                        />
                    ),
                });

                dispatch(
                    fetchReferredDriversAction(
                        1,
                        10
                    )
                );

                dispatch(
                    fetchRecruitedVehiclesAction(
                        1,
                        50
                    )
                );

                setVehicleDetailsOpen(
                    false
                );

            } else {

                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="error"
                            title="Update Failed"
                            description={
                                result.error ||
                                "Unable to update vehicle."
                            }
                            timestamp="Just now"
                        />
                    ),
                });

            }

            return result;
        };


    /*
    |--------------------------------------------------------------------------
    | PARTNERS
    |--------------------------------------------------------------------------
    */

    const partners =
        drivers
            .map(
                referredDriver => {

                    const fullDriver =
                        referredDriver
                            .driver
                            ?.driver_id
                            ? driverDetails?.[
                                referredDriver
                                    .driver
                                    .driver_id
                            ]
                            : null;


                    const hasDriver =
                        !!(
                            fullDriver?.driver_id ||
                            referredDriver?.driver
                                ?.driver_id
                        );


                    const status =
                        fullDriver?.status ||
                        referredDriver?.driver?.status ||
                        "Not Submitted";


                    const driver =
                        fullDriver ||
                        referredDriver;


                    const user =
                        fullDriver?.user ||
                        referredDriver;


                    const accountStatus =
                        user?.status ||
                        "Inactive";


                    const driverId =
                        fullDriver?.driver_id ||
                        referredDriver
                            ?.driver
                            ?.driver_id;


                    const hasVehicle =
                        (
                            recruitedVehicles ||
                            []
                        ).some(
                            vehicle =>
                                vehicle?.user?.user_id ===
                                user?.user_id
                        );


                    const driverMilestones =
                        milestones.filter(
                            milestone =>
                                milestone.driver_id ===
                                driverId
                        );


                    const achievedMilestones =
                        driverMilestones.filter(
                            milestone =>
                                milestone.milestone_status ===
                                "Achieved"
                        );


                    const latestMilestone =
                        achievedMilestones[
                            achievedMilestones.length - 1
                        ];


                    const referralEarned =
                        achievedMilestones.reduce(
                            (
                                sum,
                                milestone
                            ) =>
                                sum +
                                Number(
                                    milestone.commission_amount ||
                                    0
                                ),
                            0
                        );


                    const pendingDeliveryEarned =
                        pendingCommissions
                            .filter(
                                commission =>
                                    commission.record_type ===
                                        "COMMISSION" &&
                                    commission.driver_id ===
                                        driverId &&
                                    commission.commission_status ===
                                        "Pending"
                            )
                            .reduce(
                                (
                                    sum,
                                    commission
                                ) =>
                                    sum +
                                    Number(
                                        commission.commission_amount ||
                                        0
                                    ),
                                0
                            );


                    const paidDeliveryEarned =
                        payouts
                            .filter(
                                payout =>
                                    payout.commission_id &&
                                    payout.driver_id ===
                                        driverId &&
                                    payout.status ===
                                        "Paid"
                            )
                            .reduce(
                                (
                                    sum,
                                    payout
                                ) =>
                                    sum +
                                    Number(
                                        payout.amount ||
                                        0
                                    ),
                                0
                            );


                    const deliveryEarned =
                        pendingDeliveryEarned +
                        paidDeliveryEarned;


                    const totalEarned =
                        referralEarned +
                        deliveryEarned;


                    const progress =
                        driverMilestones.length >
                        0
                            ? Math.round(
                                (
                                    achievedMilestones.length /
                                    driverMilestones.length
                                ) *
                                100
                            )
                            : 0;


                    return {

                        id:
                            user.reference ||
                            driverId ||
                            user.user_id,

                        driver,

                        hasDriver,

                        hasVehicle,

                        name:
                            [
                                user.first_name,
                                user.last_name,
                            ]
                                .filter(Boolean)
                                .join(" ") ||
                            user.username ||
                            "Unknown Driver",

                        joined:
                            user?.created_at
                                ? new Date(
                                    user.created_at
                                ).toLocaleDateString(
                                    "en-GB",
                                    {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    }
                                )
                                : "—",

                        joinedAt:
                            user?.created_at ||
                            null,

                        status,

                        driverStatus:
                            status,

                        userStatus:
                            referredDriver.status,

                        milestone:
                            latestMilestone
                                ?.milestone_type ||
                            "Registration Started",

                        progress,

                        progressLabel:
                            `${achievedMilestones.length}/${driverMilestones.length}`,

                        referralEarned,

                        deliveryEarned,

                        accountStatus,

                        totalEarned,

                        earnings:
                            `P${totalEarned.toFixed(2)}`,
                    };
                }
            )
            .sort(
                (
                    a,
                    b
                ) =>
                    new Date(
                        b.joinedAt
                    ) -
                    new Date(
                        a.joinedAt
                    )
            );


    return (
        <>

            <DataTable
                title="Recruited Drivers"
                total={
                    partners.length
                }
                data={partners}
                columns={columns}
                pageSize={5}
                filters={[
                    "All",
                    "Pending",
                    "Approved",
                    "Suspended",
                ]}
                defaultFilter="All"
                emptyComponent={
                    <EmptyState
                        imageSize="180px"
                        title="No Recruited Drivers"
                        message="Drivers you recruit will appear here once they register."
                        py={12}
                    />
                }
                renderRow={partner => (

                    <Table.Row
                        key={
                            partner.id
                        }
                        cursor="pointer"
                        _hover={{
                            bg: "gray.50",
                        }}
                        onClick={() => {

                            const fullDriver =
                                partner.driver
                                    .driver
                                    ?.driver_id
                                    ? driverDetails[
                                        partner.driver
                                            .driver
                                            .driver_id
                                    ]
                                    : partner.driver;

                            setSelectedDriver(
                                fullDriver
                            );

                            setDriverOpen(
                                true
                            );
                        }}
                    >

                        <Table.Cell>

                            <HStack>

                                <Avatar.Root
                                    bg="#E8EEFF"
                                    color="#2453FF"
                                >

                                    <Avatar.Fallback>
                                        {partner.name
                                            .split(" ")
                                            .map(
                                                n =>
                                                    n[0]
                                            )
                                            .join("")}
                                    </Avatar.Fallback>

                                </Avatar.Root>


                                <VStack
                                    align="start"
                                    gap={0}
                                >

                                    <Text
                                        fontWeight="600"
                                    >
                                        {
                                            partner.name
                                        }
                                    </Text>

                                    <Text
                                        fontSize="sm"
                                        color="gray.500"
                                    >
                                        ID:{" "}
                                        {
                                            partner.id
                                        }
                                    </Text>

                                </VStack>

                            </HStack>

                        </Table.Cell>


                        <Table.Cell>
                            {
                                partner.joined
                            }
                        </Table.Cell>


                        <Table.Cell>

                            <Badge
                                colorPalette={
                                    badgeColor[
                                        partner.accountStatus
                                    ] || "gray"
                                }
                                borderRadius="full"
                                px={3}
                            >
                                {
                                    partner.accountStatus
                                }
                            </Badge>

                        </Table.Cell>


                        <Table.Cell>

                            <Badge
                                colorPalette={
                                    partner.driverStatus
                                        ? badgeColor[
                                            partner.driverStatus
                                        ] || "gray"
                                        : "gray"
                                }
                                borderRadius="full"
                                px={3}
                            >
                                {
                                    partner.driverStatus ||
                                    "Not Registered"
                                }
                            </Badge>

                        </Table.Cell>


                        <Table.Cell>

                            <PartnerMilestone
                                title={
                                    partner.milestone
                                }
                                progress={
                                    partner.progress
                                }
                                label={
                                    partner.progressLabel
                                }
                            />

                        </Table.Cell>


                        <Table.Cell
                            textAlign="end"
                        >

                            <VStack
                                align="end"
                                gap={0}
                            >

                                <Text
                                    fontWeight="700"
                                    color="#0F766E"
                                >
                                    P
                                    {partner.totalEarned.toFixed(
                                        2
                                    )}
                                </Text>

                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                >
                                    Referral: P
                                    {partner.referralEarned.toFixed(
                                        2
                                    )}
                                </Text>

                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                >
                                    Delivery Earnings: P
                                    {partner.deliveryEarned.toFixed(
                                        2
                                    )}
                                </Text>

                            </VStack>

                        </Table.Cell>


                        <Table.Cell
                            textAlign="end"
                        >

                            <Menu.Root
                                open={
                                    partner.accountStatus !==
                                    "Inactive"
                                        ? undefined
                                        : false
                                }
                            >

                                <Menu.Trigger
                                    asChild
                                >

                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        onClick={e => {

                                            e.stopPropagation();

                                            if (
                                                partner.accountStatus ===
                                                "Inactive"
                                            ) {

                                                toaster.create({
                                                    render:
                                                        () => (
                                                            <ActivityToast
                                                                type="info"
                                                                title="Driver Account Not Active"
                                                                description="Make sure the driver has activated their account before adding documents or a vehicle on their behalf."
                                                                timestamp="Just now"
                                                            />
                                                        ),
                                                });

                                            }

                                        }}
                                    >

                                        <EllipsisVertical
                                            size={18}
                                            color="#64748B"
                                        />

                                    </IconButton>

                                </Menu.Trigger>


                                <Menu.Positioner>

                                    <Menu.Content>

                                        {partner.accountStatus !==
                                            "Inactive" &&
                                            !partner.hasDriver && (

                                                <Menu.Item
                                                    value="upload-documents"
                                                    onClick={e => {

                                                        e.stopPropagation();

                                                        setSelectedDriver(
                                                            partner.driver
                                                        );

                                                        setDocumentsOpen(
                                                            true
                                                        );

                                                    }}
                                                >

                                                    <FileText
                                                        size={16}
                                                    />

                                                    Upload Driver Documents

                                                </Menu.Item>

                                            )}


                                        {partner.accountStatus !==
                                            "Inactive" &&
                                            partner.hasDriver &&
                                            !partner.hasVehicle && (

                                                <Menu.Item
                                                    value="add-vehicle"
                                                    onClick={e => {

                                                        e.stopPropagation();

                                                        setSelectedDriver(
                                                            partner.driver
                                                        );

                                                        setVehicleOpen(
                                                            true
                                                        );

                                                    }}
                                                >

                                                    <CarTaxiFront
                                                        size={16}
                                                    />

                                                    Add Driver Vehicle

                                                </Menu.Item>

                                            )}


                                        {partner.hasDriver && (

                                            <Menu.Item
                                                value="view-driver"
                                                onClick={e => {

                                                    e.stopPropagation();

                                                    setSelectedDriver(
                                                        partner.driver
                                                    );

                                                    setDriverOpen(
                                                        true
                                                    );

                                                }}
                                            >

                                                <IdCard
                                                    size={16}
                                                />

                                                View Driver Details

                                            </Menu.Item>

                                        )}


                                        {partner.hasVehicle && (

                                            <Menu.Item
                                                value="view-vehicle"
                                                onClick={e => {

                                                    e.stopPropagation();

                                                    const driverUserId =
                                                        partner.driver?.user_id ||
                                                        partner.driver?.user?.user_id ||
                                                        partner.driver?.driver?.user_id ||
                                                        partner.driver?.driver?.user?.user_id;

                                                    console.log(
                                                        "DRIVER USER ID:",
                                                        driverUserId
                                                    );

                                                    const vehicleRecord =
                                                        (
                                                            recruitedVehicles ||
                                                            []
                                                        ).find(
                                                            item =>
                                                                item?.user?.user_id ===
                                                                    driverUserId ||
                                                                item?.vehicle?.user_id ===
                                                                    driverUserId
                                                        );

                                                    console.log(
                                                        "VEHICLE RECORD:",
                                                        vehicleRecord
                                                    );

                                                    if (
                                                        !vehicleRecord?.vehicle
                                                    ) {

                                                        toaster.create({
                                                            render:
                                                                () => (
                                                                    <ActivityToast
                                                                        type="error"
                                                                        title="Vehicle Not Found"
                                                                        description="Unable to find the driver's vehicle details."
                                                                        timestamp="Just now"
                                                                    />
                                                                ),
                                                        });

                                                        return;
                                                    }

                                                    setSelectedVehicle(
                                                        vehicleRecord.vehicle
                                                    );

                                                    setVehicleDetailsOpen(
                                                        true
                                                    );

                                                }}
                                            >

                                                <Eye
                                                    size={16}
                                                />

                                                View Vehicle Details

                                            </Menu.Item>

                                        )}

                                    </Menu.Content>

                                </Menu.Positioner>

                            </Menu.Root>

                        </Table.Cell>

                    </Table.Row>

                )}
            />


            {/* VIEW DRIVER */}

           <ViewDriverDrawer
    open={isDriverOpen}
    onClose={() =>
        setDriverOpen(false)
    }
    driver={selectedDriver}
    onEdit={(driver) => {
      
    }}
    onUpdate={
        handleUpdateDriver
    }
/>

            {/* REGISTER DRIVER */}

            <AddDriverDocumentsDrawer
                open={
                    isDocumentsOpen
                }
                onClose={() =>
                    setDocumentsOpen(
                        false
                    )
                }
                driver={
                    selectedDriver
                }
                loading={
                    registeringDriver
                }
                onSubmit={
                    handleRegisterDriver
                }
            />


            {/* REGISTER VEHICLE */}

            <AddDriverVehicleDrawer
                open={
                    isVehicleOpen
                }
                onClose={() =>
                    setVehicleOpen(
                        false
                    )
                }
                driver={
                    selectedDriver
                }
                loading={
                    registeringVehicle
                }
                onSubmit={
                    handleRegisterVehicle
                }
            />


            {/* VIEW VEHICLE */}

          <ViewVehicleDrawer
    open={isVehicleDetailsOpen}
    onClose={() =>
        setVehicleDetailsOpen(false)
    }
    vehicle={selectedVehicle}
    onEdit={(vehicle) => {
       
    }}
    onUpdate={
        handleUpdateVehicle
    }
/>

        </>
    );
};


export default PartnersTable;